
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnhancedLandingPage } from "@/components/home/EnhancedLandingPage";
import { MobileLandingPage } from "@/components/home/MobileLandingPage";
import { useAuth } from '@/contexts/AuthContext';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const Index = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const isMobile = useIsMobile();
  const [error, setError] = useState<string | null>(null);

  // Allow desktop override via URL parameter for testing
  const urlParams = new URLSearchParams(window.location.search);
  const forceDesktop = urlParams.get('desktop') === 'true';

  // Handle navigation after both auth and admin status are determined
  useEffect(() => {
    // Only proceed if auth is not loading
    if (!authLoading) {
      if (isAuthenticated && user) {
        // Wait for admin status check to complete
        if (!isAdminLoading) {
          if (isSuperAdmin) {
            console.log('Super admin detected, redirecting to admin panel');
            navigate('/admin');
          } else {
            console.log('Regular user detected, redirecting to dashboard');
            navigate('/dashboard');
          }
        }
      }
      // If not authenticated, stay on landing page (no redirect needed)
    }
  }, [isAuthenticated, user, isSuperAdmin, authLoading, isAdminLoading, navigate]);

  // Show loading while checking auth status or admin status for authenticated users
  if (authLoading || (isAuthenticated && isAdminLoading)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <span className="text-lg">Checking authentication status...</span>
        <span className="text-sm text-muted-foreground mt-2">Please wait</span>
      </div>
    );
  }

  // Show error if there's an authentication error
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <div className="flex space-x-2 mt-4">
          <Button 
            onClick={() => navigate('/login')}
            variant="outline"
          >
            Go to Login
          </Button>
          <Button 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Show landing page for non-authenticated users
  // Use mobile-optimized version on mobile devices (unless desktop override is used)
  const shouldShowMobile = isMobile && !forceDesktop;
  
  return shouldShowMobile ? <MobileLandingPage /> : <EnhancedLandingPage />;
};

export default Index;
